import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { LocaleContext } from "contexts"
import { useContext } from "react"
import useTranslations from "hooks/useTranslations"

import AlternatingFeatures from "components/organisms/common/AlternatingFeatures"

export default function Features() {
  const { locale } = useContext(LocaleContext)

  const {
    schoolCommunitiesTitle,
    schoolCommunitiesStudentsLabel,
    schoolCommunitiesStudents1,
    schoolCommunitiesStudents2,
    schoolCommunitiesStudents3,
    schoolCommunitiesTeachersLabel,
    schoolCommunitiesTeachers1,
    schoolCommunitiesTeachers2,
    schoolCommunitiesTeachers3,
    schoolCommunitiesManagerLabel,
    schoolCommunitiesManager1,
    schoolCommunitiesManager2,
    schoolCommunitiesManager3,
  } = useTranslations()

  const images = useStaticQuery(graphql`
    {
      teachers_fi: file(relativePath: { eq: "schools/teachers-horiz-fi.jpg" }) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      students_fi: file(relativePath: { eq: "schools/students-horiz-fi.jpg" }) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      students_sv_fi: file(
        relativePath: { eq: "schools/students-horiz-sv-fi.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      communities_fi: file(
        relativePath: { eq: "schools/communities-horiz-fi.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      teachers_sv: file(relativePath: { eq: "schools/teachers-horiz-sv.jpg" }) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      students_sv: file(relativePath: { eq: "schools/students-horiz-sv.jpg" }) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      communities_sv: file(
        relativePath: { eq: "schools/communities-horiz-sv.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 90
            width: 800
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      teachers_en_st: file(
        relativePath: { eq: "schools/teachers-horiz-st-en.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 600
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      students_en_st: file(
        relativePath: { eq: "schools/students-horiz-st-en.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 600
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      communities_en_st: file(
        relativePath: { eq: "schools/communities-horiz-st-en.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 600
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      teachers_en_sb: file(
        relativePath: { eq: "schools/teachers-horiz-sb-en.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 600
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      students_en_sb: file(
        relativePath: { eq: "schools/students-horiz-sb-en.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 600
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
      communities_en_sb: file(
        relativePath: { eq: "schools/communities-horiz-sb-en.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 100
            width: 600
            placeholder: DOMINANT_COLOR
            layout: CONSTRAINED
          )
        }
      }
    }
  `)

  const imagesByLocaleSwitcher = {
    "fi-fi": {
      teachers: images.teachers_fi,
      students: images.students_fi,
      communities: images.communities_fi,
    },
    "sv-fi": {
      teachers: images.teachers_sv,
      students: images.students_sv_fi,
      communities: images.communities_sv,
    },
    "sv-se": {
      teachers: images.teachers_sv,
      students: images.students_sv,
      communities: images.communities_sv,
    },
    "en-us": {
      teachers: images.teachers_fi,
      students: images.students_fi,
      communities: images.communities_fi,
    },
    "en-fi": {
      teachers: images.teachers_en_st,
      students: images.students_en_st,
      communities: images.communities_en_st,
    },
    "en-us": {
      teachers: images.teachers_en_sb,
      students: images.students_en_sb,
      communities: images.communities_en_sb,
    },
  }

  const imagesByLocale = imagesByLocaleSwitcher[locale]

  const features = [
    {
      title: schoolCommunitiesStudentsLabel,
      description: [
        schoolCommunitiesStudents1,
        schoolCommunitiesStudents2,
        schoolCommunitiesStudents3,
      ],
      Image: () => <GatsbyImage image={getImage(imagesByLocale.students)} />,
    },
    {
      title: schoolCommunitiesTeachersLabel,
      description: [
        schoolCommunitiesTeachers1,
        schoolCommunitiesTeachers2,
        schoolCommunitiesTeachers3,
      ],
      Image: () => <GatsbyImage image={getImage(imagesByLocale.teachers)} />,
    },
    {
      title: schoolCommunitiesManagerLabel,
      description: [
        schoolCommunitiesManager1,
        schoolCommunitiesManager2,
        schoolCommunitiesManager3,
      ],
      Image: () => <GatsbyImage image={getImage(imagesByLocale.communities)} />,
    },
  ]

  return (
    <AlternatingFeatures features={features} title={schoolCommunitiesTitle} />
  )
}
